import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineGrid,
  Spinner,
  Banner,
  EmptyState,
} from '@shopify/polaris';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet } from '../lib/api';

interface AnalyticsMetrics {
  totalTryOns: number;
  successful: number;
  failed: number;
  successRate: number;
  basic: number;
  pro: number;
}

interface AnalyticsResponse {
  shop: string;
  period: { start: string; end: string; days: number };
  metrics: AnalyticsMetrics;
}

const EMPTY: AnalyticsMetrics = {
  totalTryOns: 0,
  successful: 0,
  failed: 0,
  successRate: 0,
  basic: 0,
  pro: 0,
};

export default function Analytics() {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<AnalyticsMetrics>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await apiGet<AnalyticsResponse>('/api/analytics?days=30');
      setMetrics(data?.metrics ?? EMPTY);
    } catch (err) {
      console.error('[Analytics] failed to load:', err);
      setError('Could not load analytics. Please reload the page.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const renderBody = () => {
    if (loading) {
      return (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <Spinner accessibilityLabel="Loading analytics" size="large" />
          </div>
        </Card>
      );
    }

    if (metrics.totalTryOns === 0) {
      return (
        <Card>
          <EmptyState
            heading="No try-on activity yet"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>
              Analytics will appear here once customers start using virtual try-on
              on your storefront over the last 30 days.
            </p>
          </EmptyState>
        </Card>
      );
    }

    return (
      <BlockStack gap="400">
        <InlineGrid columns={{ xs: 1, md: 3 }} gap="400">
          <Card>
            <BlockStack gap="200">
              <Text as="h2" variant="headingMd">Total Try-Ons</Text>
              <Text as="p" variant="heading2xl">{metrics.totalTryOns.toLocaleString()}</Text>
              <Text as="p" variant="bodyMd" tone="subdued">Last 30 days</Text>
            </BlockStack>
          </Card>
          <Card>
            <BlockStack gap="200">
              <Text as="h2" variant="headingMd">Success Rate</Text>
              <Text as="p" variant="heading2xl">{metrics.successRate}%</Text>
              <Text as="p" variant="bodyMd" tone="subdued">
                {metrics.successful.toLocaleString()} successful · {metrics.failed.toLocaleString()} failed
              </Text>
            </BlockStack>
          </Card>
          <Card>
            <BlockStack gap="200">
              <Text as="h2" variant="headingMd">By Quality</Text>
              <Text as="p" variant="heading2xl">{(metrics.basic + metrics.pro).toLocaleString()}</Text>
              <Text as="p" variant="bodyMd" tone="subdued">
                {metrics.basic.toLocaleString()} basic · {metrics.pro.toLocaleString()} pro
              </Text>
            </BlockStack>
          </Card>
        </InlineGrid>
      </BlockStack>
    );
  };

  return (
    <Page title="Analytics" backAction={{ content: 'Dashboard', onAction: () => navigate('/') }}>
      <Layout>
        {error ? (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError(null)}>
              <p>{error}</p>
            </Banner>
          </Layout.Section>
        ) : null}
        <Layout.Section>{renderBody()}</Layout.Section>
      </Layout>
    </Page>
  );
}
